import { CustomFieldFormats, type CustomOption } from '@interfaces/customField';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { UserContext } from '@providers/userContextProvider';
import { phoneValidator } from '@utils/common';
import parsePhoneNumber, { type CountryCode } from 'libphonenumber-js';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import CustomFieldRenderer, {
  type CustomRenderProps,
} from './customFieldRenderer';

/** Type for phone country codes map */
export type PhoneCountryCodesMap = Record<string, CountryCode | undefined>;

type UseCustomFieldRendererParams = {
  /** Custom options from the substep configuration */
  customOptions: CustomOption[];
  /** Custom field values from the user data (e.g., userData.customFields) */
  customFieldValues: Array<{ key: string; value?: unknown }> | undefined;
};

/** Props for renderCustomFields - CustomRenderProps minus the ones the hook provides */
type RenderCustomFieldsProps = Omit<
  CustomRenderProps,
  | 'customOptions'
  | 'invalidCustomFields'
  | 'phoneCountryCodes'
  | 'setPhoneCountryCodes'
>;

type UseCustomFieldRendererResult = {
  /** Array of field keys that are currently invalid */
  invalidCustomFields: string[];
  /**
   * Validates all custom fields, updates invalidCustomFields state, and returns array of invalid field keys.
   * Call this in your form's submit handler.
   */
  validateAllCustomFields: () => string[];
  /**
   * Renders the CustomFieldRenderer with validation props pre-wired.
   * Only pass the non-validation props.
   */
  renderCustomFields: (props: RenderCustomFieldsProps) => ReactNode;
};

/**
 * Custom hook for rendering CustomFieldRenderer with built-in validation.
 * Returns a render function with validation props pre-wired.
 * Automatically uses applicableCountries from UserContext.
 */
export const useCustomFieldRenderer = ({
  customOptions,
  customFieldValues,
}: UseCustomFieldRendererParams): UseCustomFieldRendererResult => {
  const { activeOrganization } = useContext(UserContext);
  const [invalidCustomFields, setInvalidCustomFields] = useState<string[]>([]);
  const [phoneCountryCodes, setPhoneCountryCodes] =
    useState<PhoneCountryCodesMap>({});

  const applicableCountries =
    activeOrganization?.applicableLocalization?.countries
      ?.map((c) => c.toLowerCase())
      ?.map((code) => code.toUpperCase()) as CountryCode[] | undefined;

  // Initialize phone country codes from existing phone values
  useEffect(() => {
    const phoneOptions = customOptions.filter(
      (opt) =>
        opt.customField?.format === CustomFieldFormats.PHONE && opt.enabled,
    );

    if (phoneOptions.length === 0) return;

    const initialCodes: Record<string, CountryCode | undefined> = {};

    for (const option of phoneOptions) {
      const fieldKey = option.customField?.key;
      if (!fieldKey) continue;

      const phoneValue = customFieldValues?.find((i) => i.key === fieldKey)
        ?.value as string | undefined;

      if (phoneValue) {
        try {
          const parsed = parsePhoneNumber(
            `+${phoneValue}`,
            applicableCountries?.at(0) ?? 'US',
          );
          const countryCode = parsed?.country || applicableCountries?.at(0);
          if (countryCode) {
            initialCodes[fieldKey] = countryCode;
          }
        } catch {
          // Parsing failed, skip this field
        }
      }
    }

    setPhoneCountryCodes((prev) => ({ ...prev, ...initialCodes }));
  }, [customOptions, customFieldValues]);

  const validateAllCustomFields = (): string[] => {
    const invalidFields: string[] = [];
    const currentPhoneCountryCodes = phoneCountryCodes;

    for (const option of customOptions) {
      if (!option.enabled) continue;
      const fieldKey = option.customField?.key;
      if (!fieldKey) continue;

      const fieldValue = customFieldValues?.find(
        (i) => i.key === fieldKey,
      )?.value;

      const isValid = validateCustomFieldValue(option, fieldValue, {
        applicableCountries: applicableCountries as CountryCodes[] | undefined,
        countryCode: currentPhoneCountryCodes[fieldKey],
      });

      if (!isValid) {
        invalidFields.push(fieldKey);
      }
    }

    setInvalidCustomFields(invalidFields);

    return invalidFields;
  };

  const renderCustomFields = useCallback(
    (props: RenderCustomFieldsProps): ReactNode => (
      <CustomFieldRenderer
        {...props}
        customOptions={customOptions}
        invalidCustomFields={invalidCustomFields}
        phoneCountryCodes={phoneCountryCodes}
        setPhoneCountryCodes={setPhoneCountryCodes}
      />
    ),
    [customOptions, invalidCustomFields, phoneCountryCodes],
  );

  return {
    invalidCustomFields,
    validateAllCustomFields,
    renderCustomFields,
  };
};

type ValidateFieldValueOptions = {
  /** Applicable countries for phone validation */
  applicableCountries?: CountryCodes[];
  /** Country code selected by user (if available) */
  countryCode?: CountryCode;
};

/**
 * Validates a custom field value based on its format.
 * Returns true if valid, false if invalid.
 * Returns true for empty values (required field validation is handled separately).
 */
export const validateCustomFieldValue = (
  customOption: CustomOption,
  value: unknown,
  options?: ValidateFieldValueOptions,
): boolean => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  const format = customOption.customField?.format;

  switch (format) {
    case CustomFieldFormats.PHONE:
      return phoneValidator(
        value as string,
        options?.countryCode,
        options?.applicableCountries,
      );
    // Add more format validations here as needed
    default:
      return true;
  }
};

export default useCustomFieldRenderer;
